import {useState, useCallback, useEffect} from 'react';
import {SpanIcon} from '@mazraeapp/react_icon_kit';
import {ShowFormError} from '../../../../utilities/helper/showFormError';
import {FormikProps} from 'formik';
import {IProductForm} from './productFormData';
import ImageCropper from '../../../../components/ImageCropper';

interface ProductImagesProps {
  form: FormikProps<IProductForm>;
  onImagesChange?: (files: File[]) => void;
}

const ProductImages = ({onImagesChange, form}: ProductImagesProps) => {
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);

  const handleCropComplete = useCallback(
    (croppedImage: File) => {
      setImageFiles(prev => [...prev, croppedImage]);

      // Create preview URL
      const previewUrl = URL.createObjectURL(croppedImage);
      setPreviewImages(prev => [...prev, previewUrl]);

      // Notify parent component about the changes
      if (onImagesChange) {
        onImagesChange([...imageFiles, croppedImage]);
      }
    },
    [imageFiles, onImagesChange],
  );

  // Adding current files into form fields
  useEffect(() => {
    form.setFieldValue('imagesFile', imageFiles);
  }, [imageFiles]);

  const handleRemoveImage = useCallback(
    (index: number) => {
      const updatedPreviews = [...previewImages];
      const updatedFiles = [...imageFiles];

      // Revoke the object URL to avoid memory leaks
      URL.revokeObjectURL(updatedPreviews[index]);

      updatedPreviews.splice(index, 1);
      updatedFiles.splice(index, 1);

      setPreviewImages(updatedPreviews);
      setImageFiles(updatedFiles);

      if (onImagesChange) {
        onImagesChange(updatedFiles);
      }
    },
    [previewImages, imageFiles, onImagesChange],
  );

  return (
    <div className="space-y-4">
      <p className="mb-2 color-gray-1 text-md">
        تصویر محصول <span className="text-red-500">*</span>
      </p>

      <div className="flex flex-wrap gap-3">
        {/* Preview thumbnails */}
        {previewImages.map((preview, index) => (
          <div key={index} className="relative">
            <img
              src={preview}
              alt={`Preview ${index}`}
              className="w-20 h-20 rounded-xl object-cover border border-gray-200"
            />
            <button
              type="button"
              onClick={() => handleRemoveImage(index)}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
              aria-label="Remove image"
            >
              <SpanIcon className="icon-nameClose-remove text-md" />
            </button>
          </div>
        ))}

        {/* ImageCropper component */}
        <ImageCropper onCropComplete={handleCropComplete} />
      </div>

      <ShowFormError field="imagesFile" form={form} />
    </div>
  );
};

export default ProductImages;
